import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight, Mail, Phone, MapPin } from 'lucide-react';
import { Button } from './ui/Button';
import { Logo } from './ui/Logo';
import { WhatsAppChat } from './WhatsAppChat';
import { CFA_INFO, SITE_LAST_UPDATED, QUALIOPI_LOGO } from '../data/constants';
const NAV_LINKS = [{
  name: 'Accueil',
  path: '/'
}, {
  name: 'Notre CFA',
  path: '/le-cfa'
}, {
  name: 'Vie étudiante',
  path: '/bde'
}, {
  name: 'CAP 360',
  path: '/candidater#cap-360-section' // Redirige vers la section CAP 360
}, {
  name: 'Formations',
  path: '/formations'
}, {
  name: 'Admissions',
  path: '/candidater'
}, {
  name: 'Entreprises',
  path: '/entreprises'
}, {
  name: 'Qualité & engagements',
  path: '/qualite'
}];
export function Layout() {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);
  return <div className="min-h-screen flex flex-col bg-background font-sans" data-id="element-375">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60" data-id="element-376">
        <div className="container flex h-20 items-center justify-between" data-id="element-377">
          <Logo data-id="element-378" />

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-6" data-id="element-379">
            {NAV_LINKS.map(link => <Link key={link.path} to={link.path} className={`text-sm font-medium transition-colors hover:text-primary ${location.pathname === link.path || link.path !== '/' && location.pathname.startsWith(link.path) ? 'text-primary' : 'text-muted-foreground'}`} data-id="element-380">
                {link.name}
              </Link>)}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-3" data-id="element-381">
            <Button variant="outline" asChild data-id="element-382">
              <Link to="/entreprises" data-id="element-383">Recruter un alternant</Link>
            </Button>
            <Button asChild data-id="element-384">
              <Link to="/candidater" data-id="element-385">Candidater</Link>
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <Button variant="ghost" size="icon" aria-label="Menu" className="lg:hidden" onClick={() => setIsMobileMenuOpen(true)} data-id="element-386">
            <Menu className="h-6 w-6" data-id="element-387" />
          </Button>
        </div>
      </header>

      {/* Custom Mobile Menu Overlay */}
      {isMobileMenuOpen && <div className="fixed inset-0 w-[100vw] min-h-[100dvh] overflow-y-auto isolate lg:hidden" style={{
      backgroundColor: '#FFFFFF',
      opacity: 1,
      zIndex: 99999
    }} data-id="element-388">
          <div className="flex flex-col min-h-full p-6" data-id="element-389">
            {/* Header du menu */}
            <div className="flex items-center justify-between mb-8" data-id="element-390">
              <Logo showTagline={false} data-id="element-391" />
              <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)} aria-label="Fermer le menu" data-id="element-392">
                <X className="h-8 w-8 text-primary-dark" data-id="element-393" />
              </Button>
            </div>

            {/* Liens du menu */}
            <nav className="flex flex-col gap-6 flex-1" data-id="element-394">
              {NAV_LINKS.map(link => <Link key={link.name} to={link.path} onClick={() => setIsMobileMenuOpen(false)} className={`text-2xl font-bold font-heading transition-colors hover:text-brand-orange ${location.pathname === link.path || link.path !== '/' && location.pathname.startsWith(link.path) ? 'text-primary' : 'text-primary-dark'}`} data-id="element-395">
                  {link.name}
                </Link>)}
            </nav>

            {/* Boutons d'action */}
            <div className="flex flex-col gap-4 mt-12 pb-8" data-id="element-396">
              <Button size="lg" variant="outline" className="w-full justify-center border-primary/20 text-primary-dark hover:bg-primary/5 text-lg py-6" asChild onClick={() => setIsMobileMenuOpen(false)} data-id="element-397">
                <Link to="/entreprises" data-id="element-398">Recruter un alternant</Link>
              </Button>
              <Button size="lg" className="w-full justify-center bg-brand-orange hover:bg-[#E67E22] text-white text-lg py-6 shadow-xl" asChild onClick={() => setIsMobileMenuOpen(false)} data-id="element-399">
                <Link to="/candidater" data-id="element-400">Candidater</Link>
              </Button>
            </div>
          </div>
        </div>}

      {/* Main Content */}
      <main className="flex-1" data-id="element-401">
        <Outlet data-id="element-402" />
      </main>

      {/* Footer */}
      <footer className="bg-primary-dark text-lavande pt-20 pb-10 border-t-4 border-primary" data-id="element-403">
        <div className="container grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16" data-id="element-404">
          {/* Identity */}
          <div className="space-y-6" data-id="element-405">
            <Logo light className="mb-2" data-id="element-406" />
            <div className="space-y-2 text-sm text-lavande/80" data-id="element-407">
              <p className="font-bold text-white" data-id="element-408">{CFA_INFO.legalName}</p>
              <p data-id="element-409">SIRET : {CFA_INFO.siret}</p>
              <p data-id="element-410">UAI : {CFA_INFO.uai}</p>
              <p data-id="element-411">NDA : {CFA_INFO.nda}</p>
              <p data-id="element-412">Président : {CFA_INFO.president}</p>
            </div>

            {/* Qualiopi certificate */}
            <div className="flex pt-2" data-id="element-413">
              <span className="inline-flex items-center justify-center bg-white rounded-xl p-4 shadow-sm" data-id="element-414">
                <img src={QUALIOPI_LOGO} alt="Certification Qualiopi — processus certifié, République Française" className="w-44 h-auto object-contain" data-id="element-415" />
              </span>
            </div>
          </div>

          {/* Contact */}
          <div className="space-y-6" data-id="element-416">
            <h3 className="font-heading font-bold text-lg text-white" data-id="element-417">
              Contact
            </h3>
            <ul className="space-y-4 text-sm" data-id="element-418">
              <li className="flex items-start gap-3" data-id="element-419">
                <div className="w-8 h-8 rounded-full bg-primary/40 flex items-center justify-center shrink-0" data-id="element-420">
                  <MapPin className="w-4 h-4 text-white" data-id="element-421" />
                </div>
                <span className="pt-1.5" data-id="element-422">{CFA_INFO.address}</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-423">
                <div className="w-8 h-8 rounded-full bg-primary/40 flex items-center justify-center shrink-0" data-id="element-424">
                  <Phone className="w-4 h-4 text-white" data-id="element-425" />
                </div>
                <span className="font-medium text-white" data-id="element-426">{CFA_INFO.phone}</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-427">
                <div className="w-8 h-8 rounded-full bg-primary/40 flex items-center justify-center shrink-0" data-id="element-428">
                  <Mail className="w-4 h-4 text-white" data-id="element-429" />
                </div>
                <a href={`mailto:${CFA_INFO.email}`} className="hover:text-white transition-colors font-medium" data-id="element-430">
                  {CFA_INFO.email}
                </a>
              </li>
            </ul>
          </div>

          {/* Navigation */}
          <div className="space-y-6" data-id="element-431">
            <h3 className="font-heading font-bold text-lg text-white" data-id="element-432">
              Navigation
            </h3>
            <ul className="space-y-3 text-sm font-medium" data-id="element-433">
              <li data-id="element-434">
                <Link to="/" className="hover:text-white transition-colors flex items-center gap-2" data-id="element-435">
                  <ChevronRight className="w-3 h-3 text-brand-orange" data-id="element-436" /> Accueil
                </Link>
              </li>
              <li data-id="element-437">
                <Link to="/le-cfa" className="hover:text-white transition-colors flex items-center gap-2" data-id="element-438">
                  <ChevronRight className="w-3 h-3 text-brand-orange" data-id="element-439" /> Le CFA
                </Link>
              </li>
              <li data-id="element-440">
                <Link to="/bde" className="hover:text-white transition-colors flex items-center gap-2" data-id="element-441">
                  <ChevronRight className="w-3 h-3 text-brand-orange" data-id="element-442" /> Le BDE
                </Link>
              </li>
              <li data-id="element-443">
                <Link to="/formations" className="hover:text-white transition-colors flex items-center gap-2" data-id="element-444">
                  <ChevronRight className="w-3 h-3 text-brand-orange" data-id="element-445" /> Nos
                  formations
                </Link>
              </li>
              <li data-id="element-446">
                <Link to="/candidater" className="hover:text-white transition-colors flex items-center gap-2" data-id="element-447">
                  <ChevronRight className="w-3 h-3 text-brand-orange" data-id="element-448" />{' '}
                  Candidater
                </Link>
              </li>
              <li data-id="element-449">
                <Link to="/entreprises" className="hover:text-white transition-colors flex items-center gap-2" data-id="element-450">
                  <ChevronRight className="w-3 h-3 text-brand-orange" data-id="element-451" /> Espace
                  Entreprises
                </Link>
              </li>
              <li data-id="element-452">
                <Link to="/contact" className="hover:text-white transition-colors flex items-center gap-2" data-id="element-453">
                  <ChevronRight className="w-3 h-3 text-brand-orange" data-id="element-454" /> Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Informations utiles */}
          <div className="space-y-6" data-id="element-455">
            <h3 className="font-heading font-bold text-lg text-white" data-id="element-456">
              Informations utiles
            </h3>
            <ul className="space-y-3 text-sm font-medium" data-id="element-457">
              <li data-id="element-458">
                <Link to="/qualite" className="hover:text-white transition-colors flex items-center gap-2" data-id="element-459">
                  <ChevronRight className="w-3 h-3 text-brand-orange" data-id="element-460" /> Qualité
                  & Qualiopi
                </Link>
              </li>
              <li data-id="element-461">
                <Link to="/accessibilite" className="hover:text-white transition-colors flex items-center gap-2" data-id="element-462">
                  <ChevronRight className="w-3 h-3 text-brand-orange" data-id="element-463" />{' '}
                  Accessibilité Handicap
                </Link>
              </li>
              <li data-id="element-464">
                <Link to="/reclamations" className="hover:text-white transition-colors flex items-center gap-2" data-id="element-465">
                  <ChevronRight className="w-3 h-3 text-brand-orange" data-id="element-466" />{' '}
                  Réclamations
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="container pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-lavande/60" data-id="element-467">
          <div className="flex flex-col items-center md:items-start gap-1" data-id="element-468">
            <p className="font-medium" data-id="element-469">
              © {new Date().getFullYear()} {CFA_INFO.name}. Tous droits
              réservés.
            </p>
            <p className="text-lavande/50" data-id="element-470">
              Site mis à jour le {SITE_LAST_UPDATED}
            </p>
          </div>
          <div className="flex items-center gap-6 font-medium" data-id="element-471">
            <Link to="/mentions-legales" className="hover:text-white transition-colors" data-id="element-472">
              Mentions légales
            </Link>
            <Link to="/confidentialite" className="hover:text-white transition-colors" data-id="element-473">
              Politique de confidentialité
            </Link>
            <Link to="/cgv" className="hover:text-white transition-colors" data-id="element-474">
              CGV
            </Link>
          </div>
        </div>
      </footer>

      <WhatsAppChat data-id="element-475" />
    </div>;
}